import {
  type AuditLogEntry,
  isAnnotationAutomationBlockerChange,
  isAnnotationQueueIdChange,
  isAnnotationStatusChange,
} from '@rossum/api-client/auditLogs';
import type { ActivityRow, ActivityRowContext } from '../../core';
import { isOpenSessionTransition } from './reviewingSession';

// "Document" block rows: Status, Queue, Automation blocker (confirmed shapes, iteration 1).
// Parsed-but-not-rendered fields (assignees, document filename) and unknown fields are dropped.
export const toDocumentRows = (
  entry: AuditLogEntry,
  context: ActivityRowContext
): ActivityRow[] => {
  const changes = entry.content.changes.annotation?.data ?? [];
  const keyPrefix = entry.timestamp;
  // Queue display names (queue id -> name): observed in the content container's meta
  // (2026-08-03); with the annotation `{ data, meta }` envelope (2026-08-06) they may
  // move to annotation meta, so check both.
  const queueNames =
    entry.content.changes.annotation?.meta?.queueNames ??
    entry.content.changes.content?.meta?.queueNames ??
    {};
  const queueName = (id: number | null) =>
    id === null ? null : (queueNames[String(id)] ?? null);

  return changes.flatMap((change): ActivityRow[] => {
    if (isAnnotationStatusChange(change)) {
      if (isOpenSessionTransition(change, entry, context)) return [];
      return [
        {
          kind: 'status',
          key: `${keyPrefix}:status`,
          previousValue: change.previousValue,
          value: change.value,
        },
      ];
    }
    if (isAnnotationQueueIdChange(change)) {
      return [
        {
          kind: 'queue',
          key: `${keyPrefix}:queue_id`,
          previousQueueId: change.previousValue,
          queueId: change.value,
          previousQueueName: queueName(change.previousValue),
          queueName: queueName(change.value),
        },
      ];
    }
    if (isAnnotationAutomationBlockerChange(change)) {
      const blocker = change.value;
      if (!blocker) return [];
      return [
        {
          kind: 'automationBlocker',
          key: `${keyPrefix}:automation_blocker`,
          blockerType: blocker.type,
          fields: blocker.fields,
        },
      ];
    }
    return [];
  });
};
